import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import LoadingScreen from "../Utils/LoadingScreen";
import { Plus, Trash } from "lucide-react";
import useNotification from "../../Sevices/useNotification";  

const ViewBanner = () => {
  const {
    fetchBanners,
    uploadBannerImage,
    deleteBanner,
    insertBanner,
  } = useNotification();

  const [Loading, setLoading] = useState(false);
  const [bannersData, setBannersData] = useState([]);
  const [isCreatingBanner, setIsCreatingBanner] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState({
    imageURL: "",
    imageTitle: "",
    imageDesctription: "",
    backgroundColor: "#ffffff",
    titleColor: "#000000",
    descriptionColor: "#666666",
  });

  useEffect(() => {
    fetchBanners(setBannersData, setLoading);
  }, []);

  const resetForm = () => {
    setForm({
      imageURL: "",
      imageTitle: "",
      imageDesctription: "",
      backgroundColor: "#ffffff",
      titleColor: "#000000",
      descriptionColor: "#666666",
    });
    setSelectedImage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const uploadedUrl = await uploadBannerImage(file);
    setUploadingImage(false);

    if (uploadedUrl) {
      setForm({ ...form, imageURL: uploadedUrl });
      setSelectedImage(uploadedUrl);
    }
  };

  const handleDelete = (id) => {
    const ok = window.confirm("Are you sure you want to delete this banner?");
    if (!ok) return;

    deleteBanner(
      id,
      () => fetchBanners(setBannersData, setLoading),
      setDeletingId
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.imageURL) return toast.error("Please select an image");

    await insertBanner(
      form,
      async () => {
        resetForm();
        setIsCreatingBanner(false);
        await fetchBanners(setBannersData, setLoading);
      },
      setLoading
    );
  };

  return (
    <div className="min-h-[80vh] p-6">
      {!isCreatingBanner && (
        <>
          <div className="flex items-end justify-end gap-4 mb-4">
            <button
              className="flex items-center space-x-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={() => setIsCreatingBanner(true)}
            >
              <Plus size={14} />
              <span>Create Banner</span>
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 w-full max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-center mb-6">
              All Banners
            </h2>

            {Loading ? (
              <LoadingScreen />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {bannersData.length > 0 ? (
                  bannersData.map((banner) => (
                    <div
                      key={banner.id}
                      className="rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all relative bg-white"
                      style={{
                        backgroundColor: banner.background_color || "#ffffff",
                      }}
                    >
                      {banner.image_url && (
                        <div className="relative w-full h-64 overflow-hidden">
                          <img
                            src={banner.image_url}
                            alt={banner.img_title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      <div className="p-4">
                        <h3
                          className="text-lg font-bold mb-2"
                          style={{ color: banner.title_color || "#000000" }}
                        >
                          {banner.img_title}
                        </h3>
                        <p
                          className="text-sm"
                          style={{
                            color: banner.description_color || "#666666",
                          }}
                        >
                          {banner.image_description}
                        </p>
                      </div>

                      <button
                        onClick={() => handleDelete(banner.id)}
                        disabled={deletingId === banner.id}
                        className="absolute bottom-3 right-3 bg-white/80 p-2 rounded-full shadow-md"
                      >
                        <Trash size={18} className="text-red-600" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    No banners available
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {isCreatingBanner && (
        <div className="max-w-6xl mx-auto bg-white p-6 rounded-xl shadow flex gap-6">
          <form onSubmit={handleSubmit} className="flex-1 space-y-4">
            <h2 className="text-2xl font-bold mb-4">Create Banner</h2>

            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full p-2 border rounded"
            />

            {uploadingImage && (
              <p className="text-sm text-gray-500">Uploading image...</p>
            )}

            <input
              type="text"
              name="imageTitle"
              placeholder="Title"
              value={form.imageTitle}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />

            <textarea
              name="imageDesctription"
              placeholder="Description"
              value={form.imageDesctription}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />

            <button
              type="submit"
              disabled={Loading || uploadingImage}
              className="w-full bg-blue-500 text-white p-2 rounded"
            >
              {Loading ? "Creating..." : "Create Banner"}
            </button>

            <button
              type="button"
              className="w-full bg-gray-300 text-black p-2 rounded"
              onClick={() => {
                resetForm();
                setIsCreatingBanner(false);
              }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ViewBanner;
